import React, { useState, useCallback } from "react";
import { ConfigProvider } from "antd";
import Modal from "./components/Modal";
import "react-quill/dist/quill.snow.css";
import articleService from "../../../service/article.service";
import StatusHandler from "../../../components/articles/StatusHandler";
import { useArticleManagement } from "../../../hooks/useArticleManagement";
import { useArticleNavigation } from "../../../hooks/useArticleNavigation";
import ArticleSearch from "../../../components/articles/ArticleSearch";
import ArticleTable from "../../../components/articles/ArticleTable";
import Pagination from "../../../components/articles/Pagination";
import EditArticleModal from "../../../components/articles/EditArticleModals";
import { antTheme, colors, tailwindClasses } from "../../../utils/theme";
import useUserRole from "./../../../utils/useUserRole";

const ArticlesRemanie = () => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [articleToEdit, setArticleToEdit] = useState(null);
  const userRole = useUserRole();

  // Hook de navigation réutilisable
  const { navigateToDetails } = useArticleNavigation();

  // Hook de gestion des articles avec navigation personnalisée
  const {
    isLoading,
    error,
    searchTerm,
    currentItems,
    handleSearch,
    getArticleTitle,
    getArticleAuthor,
    getArticleDate,
    fetchArticles,
    currentPage,
    handlePageChange,
    itemsPerPage,
    handleItemsPerPageChange,
    indexOfFirstItem,
    indexOfLastItem,
    totalItems,
    totalPages,
    isModalOpen,
    setIsModalOpen,
    modalTitle,
    setModalTitle,
    modalSize,
    setModalSize,
    modalContent,
  } = useArticleManagement(() => articleService.getAllByStatus(1), null, {
    enableModal: true,
    enableEdit: false,
    customNavigationHandler: navigateToDetails,
  });

  // Édition d'article
  const handleEditArticle = useCallback((article) => {
    console.log("📝 Ouverture modal édition:", {
      articleId: article.id_art,
      title: getArticleTitle(article),
      user: "HiQaody",
      timestamp: "2025-08-31 07:21:29",
    });
    setArticleToEdit(article);
    setIsEditModalOpen(true);
  }, [getArticleTitle]);

  // Gestion du succès de l'édition
  const handleEditSuccess = useCallback(() => {
    console.log("✅ Édition réussie, rechargement des données:", {
      user: "HiQaody",
      timestamp: "2025-08-31 07:21:29",
    });
    fetchArticles();
    window.location.reload();
    setTimeout(() => {
      setIsEditModalOpen(false);
    }, 1500);
  }, [fetchArticles]);

  // Fermeture du modal principal
  const handleCloseModal = useCallback(() => {
    console.log("🔄 Fermeture modal principal:", {
      user: "HiQaody",
      timestamp: "2025-08-31 07:21:29",
    });
    setIsModalOpen(false);
  }, [setIsModalOpen]);

  // Callback de mise à jour de statut
  const handleStatusUpdate = useCallback(async ({ article, newStatus, comment }) => {
    console.log("🔄 Callback mise à jour statut:", {
      articleId: article.id_art,
      newStatus,
      commentLength: comment.length,
      user: "HiQaody",
      timestamp: "2025-08-31 07:21:29",
    });

    try {
      await fetchArticles();
      setIsModalOpen(false);
      
      console.log("✅ Articles rechargés après changement statut:", {
        user: "HiQaody",
        timestamp: "2025-08-31 07:21:29",
      });
    } catch (error) {
      console.error("❌ Erreur lors du rechargement:", {
        error: error.message,
        user: "HiQaody",
        timestamp: "2025-08-31 07:21:29",
      });
    }
  }, [fetchArticles, setIsModalOpen]);

  // Configuration personnalisée pour ArticlesRemanie
  const customStatusFilter = useCallback((status) => {
    // Exclure les statuts "publié" et "en révision"
    return status.id_stat !== 5 && status.id_stat !== 4;
  }, []);

  // Règles d'avertissement spécifiques pour ArticlesRemanie
  const warningRules = {
    downgradeFromPublishable: {
      condition: (orig, new_stat) => 
        (orig === '3' || orig === 3) && (new_stat === '2' || new_stat === 2),
      title: '⚠️ Attention: Rétrogradation',
      description: 'Vous allez rétrograder un article "publiable" vers "à remanier". Cette action nécessitera une justification détaillée.',
      config: {
        title: '⚠️ Attention: Action sensible',
        text: 'Vous êtes sur le point de rétrograder un article publiable. Cette action est inhabituelle et pourrait nécessiter une justification.',
        icon: 'warning',
        confirmButtonText: 'Oui, je comprends les implications'
      }
    },
    toPublish: {
      condition: (orig, new_stat) => new_stat === '3' || new_stat === 3,
      config: {
        title: '🚀 Passage en publiable',
        text: 'Vous êtes sur le point de marquer l\'article comme publiable. Cela signifie qu\'il a passé avec succès l\'évaluation.',
        icon: 'success'
      }
    },
    toReject: {
      condition: (orig, new_stat) => new_stat === '7' || new_stat === 7,
      config: {
        title: '❌ Refus d\'article',
        text: 'Vous êtes sur le point de refuser l\'article. Cette action informera l\'auteur que son article ne répond pas aux critères de publication.',
        icon: 'warning'
      }
    }
  };

  // Suggestions de commentaires pour ArticlesRemanie
  const suggestionComments = [
    "Article remanié avec succès, prêt pour publication",
    "Révisions mineures effectuées conformément aux recommandations",
    "Méthodologie clarifiée et résultats mieux présentés",
    "Références bibliographiques mises à jour",
    "Structure de l'article améliorée",
    "Conclusion renforcée avec analyses complémentaires"
  ];

  // État de chargement
  if (isLoading) {
    console.log("⏳ Chargement des articles remaniés:", {
      user: "HiQaody",
      timestamp: "2025-08-31 07:21:29",
    });
    
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-t-2 border-[#57A863]"></div>
      </div>
    );
  }

  // État d'erreur
  if (error) {
    console.error("❌ Erreur lors du chargement des articles:", {
      error,
      user: "HiQaody",
      timestamp: "2025-08-31 07:21:29",
    });
    
    return (
      <div className={tailwindClasses.alertError}>
        <p>{error}</p>
        <button
          onClick={fetchArticles}
          className="mt-4 rounded-md bg-red-600 px-4 py-2 text-white hover:bg-red-700"
        >
          Réessayer
        </button>
      </div>
    );
  }

  console.log("🖼️ Rendu ArticlesRemanie:", {
    totalItems,
    currentPage,
    userRole,
    user: "HiQaody",
    timestamp: "2025-08-31 07:21:29",
  });

  return (
    <ConfigProvider theme={antTheme}>
      <div className="container mx-auto px-4 py-6">
        <ArticleSearch
          searchTerm={searchTerm}
          handleSearch={handleSearch}
          title="Articles remaniés"
          subtitle="Liste des articles remaniés en attente d'attribution des statuts"
        />

        <StatusHandler
          onStatusUpdate={handleStatusUpdate}
          showConfirmation={true}
          customStatusFilter={customStatusFilter}
          warningRules={warningRules}
          minimumCommentLength={10}
          suggestionComments={suggestionComments}
        >
          {({ 
            initializeStatusAttribution, 
            selectedArticle: statusSelectedArticle,
            StatusSelector,
            CommentInput, 
            StatusMessages,
            handleSubmitStatus,
            isValid,
            isSubmitting: statusIsSubmitting 
          }) => (
            <>
              <div className={`mb-6 overflow-hidden`}>
                <ArticleTable
                  currentItems={currentItems}
                  getArticleTitle={getArticleTitle}
                  getArticleAuthor={getArticleAuthor}
                  getArticleDate={getArticleDate}
                  handleActionPrimary={userRole === "Administrateur" ? undefined : (article) => {
                    console.log("🎯 Attribution statut initiée:", {
                      articleId: article.id_art,
                      currentStatus: article.id_stat,
                      user: "HiQaody",
                      timestamp: "2025-08-31 07:21:29",
                    });
                    initializeStatusAttribution(article);
                    setModalTitle("Attribuer des status");
                    setModalSize("2xl");
                    setIsModalOpen(true);
                  }}
                  handleViewDetails={navigateToDetails}
                  showEditButton={false}
                  showPrimaryButton={userRole !== "Administrateur"}
                  actionPrimaryText={userRole === "Administrateur" ? undefined : "Attribuer"}
                  handleEditArticle={handleEditArticle}
                  primaryColor={colors.primary}
                  dateColumnLabel="date de remaniement"
                />

                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  handlePageChange={handlePageChange}
                  itemsPerPage={itemsPerPage}
                  handleItemsPerPageChange={handleItemsPerPageChange}
                  indexOfFirstItem={indexOfFirstItem}
                  indexOfLastItem={indexOfLastItem}
                  totalItems={totalItems}
                  primaryColor={colors.primary}
                />
              </div>

              {/* Modal principal avec StatusHandler */}
              <Modal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                title={modalTitle}
                width={modalSize}
              >
                {modalTitle === "Attribuer des status" && statusSelectedArticle ? (
                  <div className="bg-white">
                    {/* En-tête de la modal avec informations de l'article */}
                    <div className="border-b border-gray-200 px-6 py-4">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
                            <svg
                              className="h-5 w-5 text-green-600"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="truncate text-lg font-semibold text-gray-900">
                            {getArticleTitle(statusSelectedArticle)}
                          </h3>
                          <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                            <span>Par {getArticleAuthor(statusSelectedArticle)}</span>
                            <span>•</span>
                            <span>Remanié le {getArticleDate(statusSelectedArticle)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Corps de la modal */}
                    <div className="space-y-6 px-6 py-6">
                      {statusSelectedArticle.id_stat !== 5 ? (
                        <>
                          {/* Sélection du nouveau statut */}
                          {StatusSelector}

                          {/* Section commentaire */}
                          {CommentInput}
                        </>
                      ) : (
                        // Article déjà publié
                        <div className="rounded-lg border border-green-200 bg-green-50 p-4">
                          <div className="flex items-start space-x-3">
                            <div className="flex-shrink-0">
                              <svg
                                className="mt-0.5 h-5 w-5 text-green-600"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                            </div>
                            <div className="flex-1">
                              <h5 className="text-sm font-medium text-green-900">
                                Article déjà publié
                              </h5>
                              <p className="mt-1 text-sm text-green-700">
                                Cet article est déjà publié et ne peut pas changer de
                                statut.
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Messages d'erreur et de succès */}
                      {StatusMessages}
                    </div>

                    {/* Footer avec boutons */}
                    <div className="flex justify-end gap-3 border-t bg-gray-50 px-6 py-4">
                      <button
                        className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                        onClick={handleCloseModal}
                        disabled={statusIsSubmitting}
                      >
                        Annuler
                      </button>

                      {statusSelectedArticle.id_stat !== 5 && (
                        <button
                          className={`rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm ${
                            isValid && !statusIsSubmitting
                              ? "bg-[#57A863] hover:bg-[#4a9154]"
                              : "cursor-not-allowed bg-gray-400"
                          }`}
                          onClick={() => {
                            console.log("🚀 Soumission statut:", {
                              articleId: statusSelectedArticle.id_art,
                              isValid,
                              user: "HiQaody",
                              timestamp: "2025-08-31 07:21:29",
                            });
                            handleSubmitStatus();
                          }}
                          disabled={!isValid || statusIsSubmitting}
                        >
                          {statusIsSubmitting ? (
                            <span className="flex items-center">
                              <svg
                                className="-ml-1 mr-2 h-4 w-4 animate-spin text-white"
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                              >
                                <circle
                                  className="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  strokeWidth="4"
                                ></circle>
                                <path
                                  className="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                ></path>
                              </svg>
                              Changement en cours...
                            </span>
                          ) : (
                            "Attribuer les status"
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                ) : (
                  // Autres contenus de modal (détails article, etc.)
                  modalContent
                )}
              </Modal>
            </>
          )}
        </StatusHandler>

        {/* Modal d'édition */}
        {isEditModalOpen && articleToEdit && (
          <EditArticleModal
            article={articleToEdit}
            onClose={() => {
              console.log("🔄 Fermeture modal édition:", {
                user: "HiQaody",
                timestamp: "2025-08-31 07:21:29",
              });
              setIsEditModalOpen(false);
            }}
            onSuccess={handleEditSuccess}
          />
        )}
      </div>
    </ConfigProvider>
  );
};

export default ArticlesRemanie;